import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Save, User, Lock, ArrowLeft, Building2, Upload } from 'lucide-react';
import { createPageUrl } from '../utils';

export default function Settings() {
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [businessService, setBusinessService] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessLogoUrl, setBusinessLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setPosition(profile.position || '');
      setPhone(profile.phone || '');
      setAvatarUrl(profile.avatar_url || '');
      setAvatarPreview(profile.avatar_url || '');
      setBusinessName(profile.business_name || '');
      setBusinessService(profile.business_service || '');
      setBusinessWebsite(profile.business_website || '');
      setBusinessPhone(profile.business_phone || '');
      setBusinessLogoUrl(profile.business_logo_url || '');
      setLogoPreview(profile.business_logo_url || '');
    }
  }, [profile]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return avatarUrl;

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Authentication session expired. Please sign in again.');
      }

      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      if (avatarUrl) {
        const oldPath = avatarUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('user-avatars').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(fileName, avatarFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (err: any) {
      console.error('Avatar upload error:', err);
      throw err;
    }
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !user) return businessLogoUrl;

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Authentication session expired. Please sign in again.');
      }

      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${user.id}/logo.${fileExt}`;

      if (businessLogoUrl) {
        const oldPath = businessLogoUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('business-logos').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('business-logos')
        .upload(fileName, logoFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('business-logos')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (err: any) {
      console.error('Logo upload error:', err);
      throw err;
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const newAvatarUrl = await uploadAvatar();

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          position,
          phone,
          avatar_url: newAvatarUrl || avatarUrl,
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: fullName }
      });

      if (authError) throw authError;

      if (newAvatarUrl) {
        setAvatarUrl(newAvatarUrl);
        setAvatarFile(null);
      }

      await refreshProfile();
      setMessage('Profile updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBusiness = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBusinessLoading(true);

    try {
      const newLogoUrl = await uploadLogo();

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          business_name: businessName,
          business_service: businessService,
          business_website: businessWebsite,
          business_phone: businessPhone,
          business_logo_url: newLogoUrl || businessLogoUrl,
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      if (newLogoUrl) {
        setBusinessLogoUrl(newLogoUrl);
        setLogoFile(null);
      }

      await refreshProfile();
      setMessage('Business information updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update business information');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Password updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-48 mb-4"></div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="space-y-4">
                <div className="h-4 bg-zinc-800 rounded w-32"></div>
                <div className="h-10 bg-zinc-800 rounded"></div>
                <div className="h-10 bg-zinc-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800 mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white mb-2">Account Settings</h1>
          <p className="text-zinc-400">Manage your profile and security settings</p>
        </div>

        {message && (
          <div className="mb-6 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-sm text-emerald-400">
            {message}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="border-b border-zinc-800 px-6 py-4">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-500" />
                <h2 className="text-lg font-semibold text-white">Profile Information</h2>
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                Update your account's profile information and email address.
              </p>
            </div>
            <form onSubmit={handleUpdateProfile} className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-zinc-300">
                        Full Name <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Full Name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="position" className="text-zinc-300">
                        Position
                      </Label>
                      <Input
                        id="position"
                        type="text"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Position"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-zinc-300">
                        Email <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="bg-zinc-800 border-zinc-700 text-zinc-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-zinc-300">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="Phone"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-primary-500 hover:bg-primary-600 text-white"
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">
                      Avatar <span className="text-red-400">*</span>
                    </Label>
                    <div className="flex flex-col items-center gap-4">
                      {avatarPreview ? (
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-zinc-800 border-2 border-zinc-700">
                          <img
                            src={avatarPreview}
                            alt="Avatar preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                          <User className="w-12 h-12 text-zinc-600" />
                        </div>
                      )}
                      <div className="w-full">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="bg-zinc-800 border-zinc-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-500 file:text-white hover:file:bg-primary-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="border-b border-zinc-800 px-6 py-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary-500" />
                <h2 className="text-lg font-semibold text-white">Business Information</h2>
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                Update your business information.
              </p>
            </div>
            <form onSubmit={handleUpdateBusiness} className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="text-zinc-300">
                        Name
                      </Label>
                      <Input
                        id="businessName"
                        type="text"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Business Name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessService" className="text-zinc-300">
                        Service
                      </Label>
                      <Input
                        id="businessService"
                        type="text"
                        value={businessService}
                        onChange={(e) => setBusinessService(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Service"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="businessWebsite" className="text-zinc-300">
                        Website
                      </Label>
                      <Input
                        id="businessWebsite"
                        type="url"
                        value={businessWebsite}
                        onChange={(e) => setBusinessWebsite(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessPhone" className="text-zinc-300">
                        Phone
                      </Label>
                      <Input
                        id="businessPhone"
                        type="tel"
                        value={businessPhone}
                        onChange={(e) => setBusinessPhone(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Business Phone"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={businessLoading}
                    className="bg-primary-500 hover:bg-primary-600 text-white"
                  >
                    {businessLoading ? 'Updating...' : 'Update'}
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Logo</Label>
                    <div className="flex flex-col items-center gap-4">
                      {logoPreview ? (
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-zinc-800 border-2 border-zinc-700">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                          <Building2 className="w-12 h-12 text-zinc-600" />
                        </div>
                      )}
                      <div className="w-full">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="bg-zinc-800 border-zinc-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-500 file:text-white hover:file:bg-primary-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="border-b border-zinc-800 px-6 py-4">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary-500" />
                <h2 className="text-lg font-semibold text-white">Change Password</h2>
              </div>
            </div>
            <form onSubmit={handleUpdatePassword} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-zinc-300">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-zinc-500">Must be at least 8 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-300">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={passwordLoading}
                className="bg-primary-500 hover:bg-primary-600 text-white"
              >
                <Lock className="w-4 h-4 mr-2" />
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
